/**
 * One rule for "is this code a SKU, or a pallet label?" (ADR 275).
 *
 * The kit reads a **per-pallet barcode**; the warehouse's alias table translates
 * it to a **WMS item code** where one maps (ADR 149/160). Both codes ride every
 * scan-side payload — `product` raw, `item` translated — and every surface that
 * shows one has to answer the same question. Answering it twice is how two
 * surfaces come to disagree about the same bin, so it is answered here.
 *
 * The translator maps an unmapped label to *itself* (`InventoryReadService
 * .itemTranslator`), so `item === product` does not mean "this is a SKU" — it
 * means "no alias matched". That is the whole subtlety.
 */

/** @param inCatalog whether a code appears in the warehouse's WMS product
 *  catalogue (ADR 196). Pass `() => false` where the catalogue is unavailable:
 *  the rule then degrades to alias-only, never to a guess. */
export function resolveSku(
  product: string | null | undefined,
  item: string | null | undefined,
  inCatalog: (code: string) => boolean,
): string | null {
  // An alias resolved it: `item` IS the WMS item code, catalogue or not — the
  // current snapshot simply may not carry every item an alias knows about.
  if (item && product && item !== product) return item;
  // Nothing was aliased. The scanned code may still be a real item code, and
  // only the catalogue can say so; otherwise it stays a label. Calling an
  // untranslated scan a SKU would invent a translation nobody made.
  const code = item || product;
  return code && inCatalog(code) ? code : null;
}
